// src/components/Dashboard.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { formatCurrency as formatMoney } from '../utils/currency';
import { DashboardSkeleton } from './SkeletonLoader';
import './dashboard.css';
import GuidedTour from './GuidedTour';
import AddExpense from '../pages/AddExpense';
import AddIncome from '../pages/AddIncome';
import SetBudget from '../pages/SetBudget';
import SavingGoal from '../pages/SavingGoal';
import { useVault } from '../context/VaultContext';
import { decryptNote } from '../services/encryption';
import VaultUnlock from './Vault/VaultUnlock';
import {
  FaWallet, FaSignOutAlt, FaUserCircle, FaChevronDown,
  FaMoneyBillWave, FaChartLine, FaPiggyBank,
  FaHandHoldingUsd, FaBullseye, FaChartBar,
  FaBrain, FaArrowUp, FaCalendarAlt, FaClock,
  FaSync, FaHome, FaExchangeAlt,
  FaCog, FaChartPie, FaMagic, FaTrophy,
  FaLock, FaUnlock, FaFire, FaStar
} from 'react-icons/fa';
import { Line, Pie } from 'react-chartjs-2';
import { toast } from 'react-hot-toast';
import { handleGamificationReward } from '../utils/RewardCelebration';
import { calculateLevel } from '../utils/gamificationConstants';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBalance: 0,
    spentThisMonth: 0,
    incomeThisMonth: 0,
    budgetLeft: 0,
    savings: 0,
    monthlyBudget: 0,
    budgetUsedPercentage: 0,
    expenseTrend: 0,
  });
  const [timeOfDay, setTimeOfDay] = useState("");
  const [currentDate, setCurrentDate] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const hasPromptedTourRef = useRef(false);
  const refreshingRef = useRef(false);
  // const { isDark, toggleTheme } = useTheme(); // CACHE BUST TEMPORARY COMMENT

  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, loading: authLoading, logout, reloadUser } = useAuth();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll Lock for Mobile Menu
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Modal states
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [showAddIncomeModal, setShowAddIncomeModal] = useState(false);
  const [showSetBudgetModal, setShowSetBudgetModal] = useState(false);
  const [showSavingsGoalModal, setShowSavingsGoalModal] = useState(false);
  const [showVaultUnlock, setShowVaultUnlock] = useState(false);

  // Vault mechanics
  const { isUnlocked, cryptoKey } = useVault();
  const [decryptedNotes, setDecryptedNotes] = useState({}); // { txId: "Decrypted Text" }

  const handleUnlockVaultSuccess = async () => {
    // Automatic decryption handled by a useEffect relying on cryptoKey state change 
    // or we could manually iterate. We'll leave it to user interaction for now.
  };

  const handleDecryptClick = async (tx) => {
    if (!isUnlocked || !cryptoKey) {
      setShowVaultUnlock(true);
      return;
    }
    try {
      const plainText = await decryptNote(tx.encryptedData, cryptoKey);
      setDecryptedNotes(prev => ({ ...prev, [tx._id]: plainText }));
    } catch (err) {
      toast.error("Decryption failed. Invalid vault session.");
    }
  };

  // Data states
  // const [stats, setStats] = useState({
  //   totalBalance: 0,
  //   spentThisMonth: 0,
  //   incomeThisMonth: 0,
  //   budgetLeft: 0,
  //   savings: 0,
  //   monthlyBudget: 0,
  //   budgetUsedPercentage: 0,
  //   expenseTrend: 0
  // });

  const [recentTransactions, setRecentTransactions] = useState([]);
  const [categorySpending, setCategorySpending] = useState([]);
  const [weeklyExpenses, setWeeklyExpenses] = useState([]);
  const [savingsGoals, setSavingsGoals] = useState([]);
  const [showDemoData, setShowDemoData] = useState(false);

  // Navigation items with proper routes
  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: FaHome, path: "/dashboard" },
    {
      id: "transactions",
      label: "Transactions",
      icon: FaExchangeAlt,
      path: "/transactions",
    },
    { id: "budget", label: "Budget", icon: FaChartPie, path: "/budget" },
    { id: "goals", label: "Goals", icon: FaBullseye, path: "/goals" },
    { id: "reports", label: "Reports", icon: FaChartBar, path: "/reports" },
    { id: "profile-top", label: "Profile", icon: FaUserCircle, path: "/profile" },
  ];

  const profileNavItems = [
    { id: "goals-profile", label: "Goals", icon: FaBullseye, path: "/goals" },
    { id: "wallets", label: "Wallets", icon: FaWallet, path: "/wallets" },
    { id: "subscriptions", label: "Subscriptions", icon: FaCog, path: "/subscriptions" },
    { id: "settings", label: "Settings", icon: FaCog, path: "/settings" },
  ];

  const mobileNavItems = [
    ...navItems,
    ...profileNavItems,
    { id: "profile", label: "Profile", icon: FaUserCircle, path: "/profile" },
    { id: "rewards", label: "Rewards", icon: FaTrophy, path: "/gamification" },
  ];

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async (isForced = false) => {
    if (refreshingRef.current && !isForced) return;
    refreshingRef.current = true;
    setRefreshing(true);
    try {
      console.log('???? Fetching dashboard data...');

      const dashboardRes = await api.get('/api/dashboard/summary');
      const dashboardData = dashboardRes.data;

      console.log("📋 Dashboard API Response:", dashboardData);

      if (dashboardData.success) {
        const statsData = dashboardData.stats || {};

        setStats({
          totalBalance: statsData.totalBalance || 0,
          spentThisMonth: statsData.monthlyExpenses || 0,
          incomeThisMonth: statsData.monthlyIncome || 0,
          budgetLeft: statsData.budgetLeft || 0,
          savings: statsData.totalSavings || 0,
          monthlyBudget: statsData.monthlyBudget || 0,
          budgetUsedPercentage: statsData.budgetUsedPercentage || 0,
          expenseTrend: dashboardData.expenseTrend || 0,
        });

        // Transactions
        setRecentTransactions(dashboardData.recentTransactions || []);

        // Category spending
        setCategorySpending(dashboardData.categorySpending || []);

        // Weekly expenses
        setWeeklyExpenses(dashboardData.weeklyExpenses || []);

        // Savings goals
        setSavingsGoals(dashboardData.savingsGoals || []);

        // Update timestamp
        setLastUpdated(
          new Date().toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
        );
      }
    } catch (err) {
      // Interceptor handles the toast
    } finally {
      refreshingRef.current = false;
      setRefreshing(false);
    }
  }, []);

  // ============ AUTH & DATA FETCHING ============
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (authLoading) {
          return;
        }

        // if (!authUser) {
        //   navigate('/login');
        //   return;
        // }

        // Setup initial user
        setUser(authUser);

        // Set time greeting
        const hour = new Date().getHours();
        if (hour < 12) setTimeOfDay('Morning');
        else if (hour < 17) setTimeOfDay('Afternoon');
        else setTimeOfDay('Evening');

        // Set current date
        const now = new Date();
        const options = { month: 'long', day: 'numeric', year: 'numeric' };
        setCurrentDate(now.toLocaleDateString('en-US', options));

      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [authUser, authLoading]);

  // Initial fetch and manual refreshes decoupled
  useEffect(() => {
    if (!authLoading && authUser) {
      fetchDashboardData();
    }
  }, [authLoading, authUser, fetchDashboardData]);

  // ============ HANDLERS ============
  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setShowUserMenu(false);
  };

  const handleSuccess = async () => {
    setShowAddExpenseModal(false);
    setShowAddIncomeModal(false);
    setShowSetBudgetModal(false);
    setShowSavingsGoalModal(false);
    await fetchDashboardData(true);
  };

  const isActive = (path) => {
    // Handle dashboard path
    if (path === "/dashboard" && location.pathname === "/") return true;
    if (path === "/dashboard" && location.pathname === "/dashboard")
      return true;
    // Handle other paths
    if (path !== "/dashboard" && location.pathname.startsWith(path))
      return true;
    return false;
  };

  const handleAddExpense = async (expenseData) => {
    try {
      console.log("??? Adding expense:", expenseData);

      const response = await api.post("/api/transactions", expenseData);

      console.log("✅ Expense response:", response.data);

      if (response.data.success) {
        setShowAddExpenseModal(false);
        await fetchDashboardData(true);
        if (response.data.gamification) {
          handleGamificationReward(response.data.gamification);
          if (reloadUser) await reloadUser();
        }
        toast.success('Expenses added successfully.', {
          style: {
            background: "#16a34a",
            color: "#ffffff",
          },
          iconTheme: {
            primary: "#bbf7d0",
            secondary: "#166534",
          },
        });
      }
    } catch (err) {
      console.error("❌ Failed to add expense:", err);
      alert("Failed to add expense. Please try again.");
    }
  };

  const handleAddIncome = async (incomeData) => {
    try {
      console.log("??? Adding income:", incomeData);

      const response = await api.post("/api/transactions", incomeData);

      console.log("✅ Income response:", response.data);

      if (response.data.success) {
        setShowAddIncomeModal(false);
        await fetchDashboardData(true);
        if (response.data.gamification) {
          handleGamificationReward(response.data.gamification);
          if (reloadUser) await reloadUser();
        }
        toast.success('Income Added Successfully.', {
          style: {
            background: "#16a34a",
            color: "#ffffff",
          },
          iconTheme: {
            primary: "#bbf7d0",
            secondary: "#166534",
          },
        });
      }
    } catch (err) {
      console.error("❌ Failed to add income:", err);
      alert("Failed to add income. Please try again.");
    }
  };

  const handleAIInsights = () => {
    navigate("/behaviour-analysis");
  };

  const handleOpenGoals = () => {
    navigate("/goals", { state: { refetchAt: Date.now() } });
  };

  const tourSteps = [
    {
      target: '[data-tour="dashboard-header"]',
      title: 'Welcome to WalletWise',
      content: 'This is your command center for budget, spending, and savings.'
    },
    {
      target: '[data-tour="refresh-btn"]',
      title: 'Refresh Data',
      content: 'Use this button to pull the latest transactions and stats instantly.'
    },
    {
      target: '[data-tour="ai-insights-btn"]',
      title: 'AI Insights',
      content: 'Open behavior analysis to see trends, warnings, and suggestions.'
    },
    {
      target: '[data-tour="decision-helper-btn"]',
      title: 'Decision Maker',
      content: 'Check affordability before purchases with your budget and context.'
    },
    {
      target: '[data-tour="quick-stats"]',
      title: 'Quick Stats',
      content: 'Track total balance, monthly spending, savings, and budget status.'
    },
    {
      target: '[data-tour="quick-actions"]',
      title: 'Quick Actions',
      content: 'Add income/expense, set budget, and jump to key tools in one click.'
    },
    {
      target: '[data-tour="charts"]',
      title: 'Charts & Forecasts',
      content: 'Visualize your pace and category-wise spending patterns.'
    },
    {
      target: '[data-tour="recent-transactions"]',
      title: 'Recent Activity',
      content: 'Review your latest transactions and validate entries quickly.'
    }
  ];

  useEffect(() => {
    if (loading || authLoading || !authUser || hasPromptedTourRef.current) return;
    hasPromptedTourRef.current = true;
    const shouldShowTour = window.sessionStorage.getItem('walletwise-show-tour-once') === 'true';
    if (shouldShowTour) {
      window.sessionStorage.removeItem('walletwise-show-tour-once');
      setIsTourOpen(true);
    }
  }, [loading, authLoading, authUser]);

  const handleTourClose = (completed) => {
    setIsTourOpen(false);
    const userId = authUser?.id || authUser?._id;
    if (completed && userId) {
      const storageKey = `walletwise-tour-completed-${userId}`;
      window.localStorage.setItem(storageKey, 'true');
    }
  };

  const demoDashboard = {
    stats: {
      totalBalance: 18450,
      spentThisMonth: 3275,
      incomeThisMonth: 12000,
      budgetLeft: 1725,
      savings: 8400,
      monthlyBudget: 5000,
      budgetUsedPercentage: 65.5,
      expenseTrend: -8,
    },
    weeklyExpenses: [
      { day: "Mon", amount: 420 },
      { day: "Tue", amount: 560 },
      { day: "Wed", amount: 310 },
      { day: "Thu", amount: 780 },
      { day: "Fri", amount: 490 },
      { day: "Sat", amount: 620 },
      { day: "Sun", amount: 95 },
    ],
    categorySpending: [
      { name: "Food", amount: 1250 },
      { name: "Transport", amount: 640 },
      { name: "Books", amount: 725 },
      { name: "Subscriptions", amount: 360 },
      { name: "Social", amount: 300 },
    ],
    savingsGoals: [
      {
        name: "Laptop Fund",
        category: "Education",
        currentAmount: 32000,
        targetAmount: 50000,
        progress: 64,
        targetDate: new Date(new Date().setDate(new Date().getDate() + 45)).toISOString(),
      },
      {
        name: "Emergency Buffer",
        category: "Safety",
        currentAmount: 9000,
        targetAmount: 15000,
        progress: 60,
        targetDate: new Date(new Date().setDate(new Date().getDate() + 90)).toISOString(),
      },
    ],
    recentTransactions: [
      { _id: "demo-1", type: "expense", category: "Food", description: "Campus lunch", amount: 180, date: new Date().toISOString() },
      { _id: "demo-2", type: "expense", category: "Transport", description: "Metro card top-up", amount: 300, date: new Date(Date.now() - 86400000).toISOString() },
      { _id: "demo-3", type: "income", category: "Freelance", description: "Design project", amount: 4500, date: new Date(Date.now() - 172800000).toISOString() },
      { _id: "demo-4", type: "expense", category: "Books", description: "Course material", amount: 725, date: new Date(Date.now() - 259200000).toISOString() },
    ],
  };

  const activeStats = showDemoData ? demoDashboard.stats : stats;
  const activeWeeklyExpenses = showDemoData ? demoDashboard.weeklyExpenses : weeklyExpenses;
  const activeCategorySpending = showDemoData ? demoDashboard.categorySpending : categorySpending;
  const activeSavingsGoals = showDemoData ? demoDashboard.savingsGoals : savingsGoals;
  const activeRecentTransactions = showDemoData ? demoDashboard.recentTransactions : recentTransactions;
  const hasLiveWeeklyExpenseData = weeklyExpenses.some((exp) => exp.amount > 0);
  const hasLiveCategorySpendingData = categorySpending.some((cat) => cat.amount > 0);

  // ============ CHART CONFIGURATIONS ============

  // Weekly expenses chart with empty state handling
  const weeklyExpensesChart = {
    labels:
      activeWeeklyExpenses.length > 0
        ? activeWeeklyExpenses.map((item) => item.day)
        : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    datasets: [
      {
        label: "Daily Expenses",
        data:
          activeWeeklyExpenses.length > 0
            ? activeWeeklyExpenses.map((item) => item.amount)
            : [0, 0, 0, 0, 0, 0, 0],
        borderColor: "#f87171",
        backgroundColor: "rgba(248, 113, 113, 0.1)",
        fill: true,
        tension: 0.4,
        borderWidth: 2,
      },
    ],
  };

  // Category spending chart with empty state
  const spendingByCategoryChart = {
    labels:
      activeCategorySpending.length > 0
        ? activeCategorySpending.map((item) => item.name)
        : ["No Data"],
    datasets: [
      {
        data:
          activeCategorySpending.length > 0
            ? activeCategorySpending.map((item) => item.amount)
            : [100],
        backgroundColor: [
          "#38bdf8",
          "#60a5fa",
          "#7dd3fc",
          "#93c5fd",
          "#a5b4fc",
          "#c4b5fd",
          "#d8b4fe",
          "#e9d5ff",
          "#f0abfc",
          "#f9a8d4",
        ],
        borderWidth: 2,
        borderColor: "#ffffff",
      },
    ],
  };

  // Cumulative Projection Chart
  const todayDate = new Date();
  const currentDayNum = todayDate.getDate();
  const daysInMonthNum = new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, 0).getDate();

  // Create an array of days from 1 to daysInMonth
  const monthLabels = Array.from({ length: daysInMonthNum }, (_, i) => i + 1);

  // Calculate daily pace
  const dailyPaceValue = activeStats.spentThisMonth / Math.max(currentDayNum, 1);

  // Actual spending up to today
  const actualData = monthLabels.map(day => {
    if (day <= currentDayNum) {
      return dailyPaceValue * day; // simplified approximation for the chart
    }
    return null; // hide tail
  });

  // Projected spending dotted line from today to end of month
  const projectedData = monthLabels.map(day => {
    if (day >= currentDayNum) {
      return dailyPaceValue * day;
    }
    return null; // hide prefix
  });

  const projectionChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: "Actual Spent",
        data: actualData,
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        fill: true,
        tension: 0.2,
        borderWidth: 3,
      },
      {
        label: "Projected Trend",
        data: projectedData,
        borderColor: "#94a3b8",
        borderDash: [5, 5], // Dotted line
        fill: false,
        tension: 0.2,
        borderWidth: 2,
      }
    ]
  };

  if (activeStats.monthlyBudget > 0) {
    projectionChartData.datasets.push({
      label: "Budget Limit",
      data: monthLabels.map(() => activeStats.monthlyBudget),
      borderColor: "#ef4444",
      borderWidth: 1,
      fill: false,
      pointRadius: 0,
      borderDash: [2, 2]
    });
  }

  const projectionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { usePointStyle: true } },
      tooltip: {
        callbacks: {
          label: function (context) {
            return context.dataset.label + ': ' + formatMoney(context.raw, user?.currency);
          }
        }
      }
    },
    scales: {
      y: { beginAtZero: true, grid: { color: "rgba(226, 232, 240, 0.5)" } },
      x: { title: { display: true, text: 'Day of Month' }, grid: { display: false } }
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 20,
          usePointStyle: true,
          color: "#334155",
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(226, 232, 240, 0.5)",
        },
        ticks: {
          color: "#64748b",
          callback: function (value) {
            return formatMoney(value, user?.currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
          }
        }
      },
      x: {
        grid: {
          color: "rgba(226, 232, 240, 0.5)",
        },
        ticks: {
          color: "#64748b",
        },
      },
    },
  };

  // ============ UTILITIES ============
  const formatCurrency = (amount) => {
    return formatMoney(amount, user?.currency, { minimumFractionDigits: 0 });
  };

  const formatTransactionDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const currentLevelInfo = calculateLevel(user?.totalXP || 0);
  const budgetStatusTone =
    activeStats.monthlyBudget === 0
      ? "neutral"
      : activeStats.budgetUsedPercentage >= 100
        ? "danger"
        : activeStats.budgetUsedPercentage >= 75
          ? "warning"
          : "good";
  const budgetStatusCopy =
    activeStats.monthlyBudget === 0
      ? "Set a budget to unlock pace tracking."
      : activeStats.budgetUsedPercentage >= 100
        ? "You have crossed your plan and need a reset."
        : activeStats.budgetUsedPercentage >= 75
          ? "You are entering the caution zone this month."
          : "Safe-to-spend room is healthy for the month.";
  const heroPulseItems = [
    {
      id: "income",
      label: "Income Landed",
      value: formatCurrency(activeStats.incomeThisMonth),
      meta: "Captured this month",
      tone: "good",
    },
    {
      id: "pace",
      label: "Daily Spend Pace",
      value: `${formatCurrency(dailyPaceValue)}/day`,
      meta: "Based on this month so far",
      tone: "cool",
    },
    {
      id: "streak",
      label: "Consistency",
      value: `${user?.currentStreak || 0} day streak`,
      meta: `Level ${currentLevelInfo.level} ${currentLevelInfo.title}`,
      tone: "warm",
    },
  ];
  const quickActions = [
    {
      id: "expense",
      title: "Add Expense",
      description: "Capture spending before it disappears into the month.",
      icon: FaMoneyBillWave,
      iconTone: "blue",
      eyebrow: "Track instantly",
      onClick: () => setShowAddExpenseModal(true),
    },
    {
      id: "income",
      title: "Add Income",
      description: "Record salary, allowance, freelance, or side-hustle cash.",
      icon: FaHandHoldingUsd,
      iconTone: "green",
      eyebrow: "Grow balance",
      onClick: () => setShowAddIncomeModal(true),
    },
    {
      id: "budget",
      title: "Set Budget",
      description: "Shape a monthly plan with clear limits and breathing room.",
      icon: FaChartLine,
      iconTone: "orange",
      eyebrow: "Control the month",
      onClick: () => setShowSetBudgetModal(true),
    },
    {
      id: "goals",
      title: "Set Goals",
      description: "Turn future plans into tracked savings milestones.",
      icon: FaBullseye,
      iconTone: "purple",
      eyebrow: "Plan ahead",
      onClick: handleOpenGoals,
    },
    {
      id: "analysis",
      title: "AI Analysis",
      description: "Spot behavior patterns and hidden pressure points fast.",
      icon: FaBrain,
      iconTone: "pink",
      eyebrow: "Understand behavior",
      onClick: handleAIInsights,
    },
    {
      id: "reports",
      title: "View Reports",
      description: "Open the full visual breakdown of your financial story.",
      icon: FaChartBar,
      iconTone: "teal",
      eyebrow: "Read the trends",
      onClick: () => navigate("/reports"),
    },
  ];

  // ============ RENDERING ============
  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="dashboard">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        >
        </div>
      )}
      {/* Clean, Focused Navbar */}
      <header className="dashboard-header">
        {/* Left: Logo */}
        <div className="nav-left">
          <Link to="/dashboard" className="logo-container">
            <FaWallet className="logo-icon" />
            <h1 className="logo-text">WalletWise</h1>
          </Link>
        </div>

        {/* Center: Navigation Links */}
        <nav className="nav-center" ref={mobileMenuRef}>
          <button
            className="mobile-menu-toggle"
            onClick={() => {
              setShowUserMenu(false);
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav-menu"
          >
            <span className={`hamburger ${isMobileMenuOpen ? "open" : ""}`}></span>
            <span className={`hamburger ${isMobileMenuOpen ? "open" : ""}`}></span>
            <span className={`hamburger ${isMobileMenuOpen ? "open" : ""}`}></span>
          </button>

          <ul
            id="mobile-nav-menu"
            className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}
          >
            <li className="mobile-drawer-only mobile-menu-header">
              <div className="mobile-menu-profile">
                <div className="mobile-menu-avatar" aria-hidden="true">
                  {user?.fullName?.charAt(0) || user?.name?.charAt(0) || "U"}
                </div>
                <div className="mobile-menu-user-meta">
                  <p className="mobile-menu-name">{user?.fullName || user?.name || "User"}</p>
                  <p className="mobile-menu-email">{user?.email || ""}</p>
                </div>
              </div>
              <div className="mobile-menu-gamification">
                <div className="mobile-gamification-pill" title="Transaction Streak">
                  <FaFire />
                  <span>{user?.currentStreak || 0} streak</span>
                </div>
                <div
                  className="mobile-gamification-pill"
                  title={`Level ${currentLevelInfo.level}: ${currentLevelInfo.title}`}
                >
                  <FaStar />
                  <span>Lvl {currentLevelInfo.level}</span>
                </div>
              </div>
            </li>

            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavigation(item.path)}
                    className={`nav-link ${active ? "active" : ""}`}
                    aria-current={active ? "page" : undefined}
                  >
                    <Icon className="nav-icon" />
                    <span>{item.label}</span>
                  </button>
                </li>
              );
            })}

            {mobileNavItems
              .filter((item) => !navItems.some((navItem) => navItem.id === item.id))
              .map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <li key={item.id} className="mobile-drawer-only">
                    <button
                      onClick={() => handleNavigation(item.path)}
                      className={`nav-link ${active ? "active" : ""}`}
                      aria-current={active ? "page" : undefined}
                    >
                      <Icon className="nav-icon" />
                      <span>{item.label}</span>
                    </button>
                  </li>
                );
              })}

            <li className="mobile-drawer-only mobile-actions-panel">
              <button
                type="button"
                className="mobile-action-btn"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  fetchDashboardData(true);
                }}
                disabled={refreshing}
              >
                <FaSync className={refreshing ? "spin" : ""} />
                <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
              </button>

              <button
                type="button"
                className="mobile-action-btn"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleAIInsights();
                }}
              >
                <FaBrain />
                <span>AI Insights</span>
              </button>

              <button
                type="button"
                className="mobile-action-btn"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate('/decision-helper');
                }}
              >
                <FaMagic />
                <span>Decision Helper</span>
              </button>

              <button
                type="button"
                className="mobile-action-btn"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsTourOpen(true);
                }}
              >
                <FaStar />
                <span>Start Tour</span>
              </button>
            </li>

            <li className="mobile-drawer-only">
              <button
                onClick={handleLogout}
                className="nav-link mobile-logout"
                type="button"
              >
                <FaSignOutAlt className="nav-icon" />
                <span>Logout</span>
              </button>
            </li>
          </ul>
        </nav>

        {/* Right: User Profile & Gamification */}
        <div className="nav-right" ref={userMenuRef}>
          <div className="gamification-stats" style={{ display: 'flex', alignItems: 'center', gap: '15px', marginRight: '15px', color: 'var(--text-secondary)' }}>
            <div className="gamification-streak" title="Transaction Streak" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <FaFire color="#f97316" />
              <span style={{ fontWeight: 600 }}>{user?.currentStreak || 0}</span>
            </div>

            <div className="gamification-level" title={`Level ${currentLevelInfo.level}: ${currentLevelInfo.title}`} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <FaStar color="#eab308" />
              <span style={{ fontWeight: 600 }}>Lvl {currentLevelInfo.level}</span>
            </div>
          </div>
          {/*
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={
              isDark ? "Switch to light theme" : "Switch to dark theme"
            }
            type="button"
          >
            {isDark ? <FaSun /> : <FaMoon />}
          </button>
          */}
          <button
            className="user-profile-trigger"
            onClick={() => setShowUserMenu(!showUserMenu)}
            aria-expanded={showUserMenu}
            aria-label="User menu"
            aria-haspopup="true"
          >
            <div className="user-avatar" aria-hidden="true">
              {user?.fullName?.charAt(0) || user?.name?.charAt(0) || "U"}
            </div>
            <FaChevronDown
              className={`dropdown-arrow ${showUserMenu ? "open" : ""}`}
            />
          </button>

          {/* User Dropdown Menu */}
          {showUserMenu && (
            <div className="user-dropdown-menu" role="menu">
              <div className="user-dropdown-header">
                <div className="dropdown-avatar">
                  {user?.fullName?.charAt(0) || user?.name?.charAt(0) || "U"}
                </div>
                <div className="dropdown-user-info">
                  <span className="dropdown-user-name">
                    {user?.fullName || user?.name}
                  </span>
                  <span className="dropdown-user-email">{user?.email}</span>
                </div>
              </div>

              <div className="dropdown-divider"></div>

              <Link
                to="/gamification"
                className="dropdown-item"
                role="menuitem"
                onClick={() => setShowUserMenu(false)}
                title="View rewards"
              >
                <FaTrophy />
                <span>Rewards</span>
              </Link>

              <div className="dropdown-divider"></div>

              <Link
                to="/dashboard"
                className="dropdown-item"
                role="menuitem"
                onClick={() => setShowUserMenu(false)}
                title="Open dashboard"
              >
                <FaHome />
                <span>Dashboard</span>
              </Link>

              <Link
                to="/transactions"
                className="dropdown-item"
                role="menuitem"
                onClick={() => setShowUserMenu(false)}
                title="Open transactions"
              >
                <FaExchangeAlt />
                <span>Transactions</span>
              </Link>

              <Link
                to="/budget"
                className="dropdown-item"
                role="menuitem"
                onClick={() => setShowUserMenu(false)}
                title="Open budget"
              >
                <FaChartPie />
                <span>Budget</span>
              </Link>

              <Link
                to="/goals"
                className="dropdown-item"
                role="menuitem"
                onClick={() => setShowUserMenu(false)}
                title="Open goals"
              >
                <FaBullseye />
                <span>Goals</span>
              </Link>

              <Link
                to="/reports"
                className="dropdown-item"
                role="menuitem"
                onClick={() => setShowUserMenu(false)}
                title="Open reports"
              >
                <FaChartBar />
                <span>Reports</span>
              </Link>

              <Link
                to="/profile"
                className="dropdown-item"
                role="menuitem"
                onClick={() => setShowUserMenu(false)}
                title="Open profile"
              >
                <FaUserCircle />
                <span>Profile</span>
              </Link>

              <Link
                to="/wallets"
                className="dropdown-item"
                role="menuitem"
                onClick={() => setShowUserMenu(false)}
                title="Open wallets"
              >
                <FaWallet />
                <span>Wallets</span>
              </Link>

              <Link
                to="/subscriptions"
                className="dropdown-item"
                role="menuitem"
                onClick={() => setShowUserMenu(false)}
                title="Open subscriptions"
              >
                <FaCog />
                <span>Subscriptions</span>
              </Link>

              <Link
                to="/settings"
                className="dropdown-item"
                role="menuitem"
                onClick={() => setShowUserMenu(false)}
                title="Open settings"
              >
                <FaCog />
                <span>Settings</span>
              </Link>

              <div className="dropdown-divider"></div>

              <button
                onClick={handleLogout}
                className="dropdown-item logout"
                role="menuitem"
                title="Logout"
              >
                <FaSignOutAlt />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content Area */}
      <div className="dashboard-content" data-tour="dashboard-header">
        {/* Dashboard Header with Greeting and Actions */}
        <div className="dashboard-header-area">
          <div className="hero-panel">
            <div className="hero-orb hero-orb-one" aria-hidden="true"></div>
            <div className="hero-orb hero-orb-two" aria-hidden="true"></div>

            <div className="dashboard-header-left">
              <span className="section-eyebrow">Financial Command Center</span>
              <h1 className="dashboard-title">Dashboard</h1>
              <div className="greeting-section">
                <h2 className="greeting-text">
                  Good {timeOfDay},{" "}
                  <span className="user-name">
                    {user?.fullName || user?.name}!
                  </span>
                </h2>

                <p className="hero-description">
                  Everything important is staged here: momentum, pressure points,
                  and the next move that keeps your month in control.
                </p>

                <p className="current-date">
                  <FaCalendarAlt className="date-icon" />
                  {currentDate}
                </p>
              </div>

              <div className="hero-pulse-grid">
                {heroPulseItems.map((item) => (
                  <div key={item.id} className={`hero-pulse-card ${item.tone}`}>
                    <span className="hero-pulse-label">{item.label}</span>
                    <strong className="hero-pulse-value">{item.value}</strong>
                    <span className="hero-pulse-meta">{item.meta}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="dashboard-header-right">
              <div className="hero-spotlight-card">
                <span className="spotlight-kicker">Live Budget Pulse</span>
                <div className={`spotlight-badge ${budgetStatusTone}`}>
                  {activeStats.monthlyBudget > 0
                    ? `${Math.round(activeStats.budgetUsedPercentage)}% used`
                    : "No budget yet"}
                </div>
                <p className="spotlight-copy">{budgetStatusCopy}</p>
                <div className="spotlight-metrics">
                  <div>
                    <span>Safe to Spend</span>
                    <strong>{formatCurrency(Math.max(activeStats.budgetLeft, 0))}</strong>
                  </div>
                  <div>
                    <span>Total Budget</span>
                    <strong>
                      {activeStats.monthlyBudget > 0 ? formatCurrency(activeStats.monthlyBudget) : "Not set"}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="action-buttons">
                <button
                  className={`refresh-btn ${refreshing ? "refreshing" : ""}`}
                  data-tour="refresh-btn"
                  onClick={fetchDashboardData}
                  disabled={refreshing}
                  aria-busy={refreshing}
                  aria-disabled={refreshing}
                  title={
                    refreshing
                      ? "Refreshing dashboard..."
                      : "Refresh dashboard data"
                  }
                >
                  <FaSync className={refreshing ? "spin" : ""} />
                  <span>{refreshing ? "Refreshing..." : "Refresh Data"}</span>
                </button>

                <button
                  className="ai-insights-btn"
                  data-tour="ai-insights-btn"
                  onClick={handleAIInsights}
                  title="View AI-powered spending insights"
                  aria-label="AI Insights"
                >
                  <FaBrain className="ai-icon" />
                  <span>AI Insights</span>
                </button>

                <button
                  className="decision-btn"
                  data-tour="decision-helper-btn"
                  onClick={() => navigate('/decision-helper')}
                  title="AI-powered purchase advisor"
                  aria-label="Decision Helper"
                >
                  <FaMagic className="ai-icon" />
                  <span>Decision Helper</span>
                </button>

                <button
                  className="tour-btn"
                  onClick={() => setIsTourOpen(true)}
                  title="Start guided product tour"
                  aria-label="Start guided product tour"
                >
                  <FaStar className="ai-icon" />
                  <span>Start Tour</span>
                </button>

                <button
                  className={`demo-toggle-btn ${showDemoData ? "active" : ""}`}
                  onClick={() => setShowDemoData((current) => !current)}
                  type="button"
                  title="Toggle portfolio demo data"
                  aria-pressed={showDemoData}
                >
                  <FaChartLine className="ai-icon" />
                  <span>{showDemoData ? "Live Data" : "Demo Data"}</span>
                </button>
              </div>
              {(lastUpdated || showDemoData) && !refreshing && (
                <p className="actions-refreshed">
                  {showDemoData ? "Portfolio demo mode" : `Refreshed ${lastUpdated}`}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        {refreshing && <div className="stats-overlay">Updating...</div>}
        <div className={`quick-stats ${refreshing ? "loading" : ""}`} data-tour="quick-stats">
          <div className="stat-card">
            <div className="stat-icon blue">
              <FaWallet />
            </div>

            <div className="stat-content">
              <h3>Liquidity Overview</h3>
              <p className="stat-value">{formatCurrency(activeStats.totalBalance)}</p>

              <div className="stat-trend">
                <FaArrowUp className="trend-up" />
                <span>
                  {formatCurrency(activeStats.incomeThisMonth)} income minus{" "}
                  {formatCurrency(activeStats.spentThisMonth)} spending
                </span>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon red">
              <FaMoneyBillWave />
            </div>

            <div className="stat-content">
              <h3>Spending Velocity</h3>
              <p className="stat-value">
                {formatCurrency(activeStats.spentThisMonth)}
              </p>

              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${Math.min(activeStats.budgetUsedPercentage, 100)}%`,
                    }}
                  ></div>
                </div>

                <span className="progress-text">
                  {activeStats.monthlyBudget > 0
                    ? `${formatCurrency(activeStats.budgetLeft)} safe to spend of ${formatCurrency(activeStats.monthlyBudget)}`
                    : "No budget set"}
                </span>
              </div>

              {activeStats.monthlyBudget === 0 && !showDemoData && (
                <button
                  onClick={() => setShowSetBudgetModal(true)}
                  className="cta-button small"
                >
                  Set your first budget →
                </button>
              )}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon green">
              <FaPiggyBank />
            </div>

            <div className="stat-content">
              <h3>Total Savings</h3>
              <p className="stat-value">{formatCurrency(activeStats.savings)}</p>

              <div className="stat-trend">
                <span>{activeSavingsGoals.length} active goals</span>
              </div>

              {activeStats.savings === 0 && !showDemoData && (
                <button
                  onClick={() => setShowSavingsGoalModal(true)}
                  className="cta-button small"
                >
                  Create a goal →
                </button>
              )}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon purple">
              <FaChartLine />
            </div>

            <div className="stat-content">
              <h3>Budget Status</h3>
              <p className="stat-value">
                {Math.round(activeStats.budgetUsedPercentage)}% used
              </p>

              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${Math.min(activeStats.budgetUsedPercentage, 100)}%`,
                    }}
                  ></div>
                </div>

                <span className="progress-text">
                  {activeStats.monthlyBudget > 0
                    ? `${formatCurrency(activeStats.monthlyBudget)} total`
                    : "No budget set"}
                </span>
              </div>

              {activeStats.monthlyBudget === 0 && !showDemoData && (
                <button
                  onClick={() => setShowSetBudgetModal(true)}
                  className="cta-button small"
                >
                  Set budget →
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions-section" data-tour="quick-actions">
          <div className="section-heading-row">
            <div>
              <span className="section-eyebrow">Move Fast</span>
              <h2 className="section-title">Quick Actions</h2>
            </div>
            <p className="section-description">
              High-frequency actions designed to keep your financial flow frictionless.
            </p>
          </div>
          <div className="quick-actions-grid">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={action.onClick}
                  className="action-card"
                >
                  <span className="action-eyebrow">{action.eyebrow}</span>
                  <div className={`action-icon ${action.iconTone}`}>
                    <Icon />
                  </div>
                  <h3>{action.title}</h3>
                  <p>{action.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Charts Section with Empty States */}
        <div className="charts-section" data-tour="charts">
          <div className="chart-container chart-feature">
            <div className="chart-header">
              <h3>Monthly Pacing & Projection</h3>
              <span className="chart-subtitle">Daily pace versus budget limit</span>
            </div>

            <div className="chart-wrapper">
              <Line data={projectionChartData} options={projectionOptions} />
            </div>
          </div>

          <div className="chart-container">
            <div className="chart-header">
              <h3>Weekly Expenses</h3>
              <div className="chart-header-actions">
                {!hasLiveWeeklyExpenseData && !showDemoData && (
                  <button
                    type="button"
                    className="sample-data-toggle"
                    onClick={() => setShowDemoData(true)}
                  >
                    Show sample data
                  </button>
                )}
                <span className="chart-subtitle">Last 7 days</span>
              </div>
            </div>

            <div className="chart-wrapper">
              {activeWeeklyExpenses.length > 0 &&
                activeWeeklyExpenses.some((exp) => exp.amount > 0) ? (
                <Line data={weeklyExpensesChart} options={chartOptions} />
              ) : (
                <div className="chart-empty-state">
                  <FaChartLine className="empty-chart-icon" />
                  <p>No expense data for this week</p>
                  <button
                    onClick={() => setShowAddExpenseModal(true)}
                    className="btn-primary small"
                  >
                    Add First Expense
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="chart-container">
            <div className="chart-header">
              <h3>Spending by Category</h3>
              <div className="chart-header-actions">
                {!hasLiveCategorySpendingData && !showDemoData && (
                  <button
                    type="button"
                    className="sample-data-toggle"
                    onClick={() => setShowDemoData(true)}
                  >
                    Show sample data
                  </button>
                )}
                <span className="chart-subtitle">This month</span>
              </div>
            </div>

            <div className="chart-wrapper">
              {activeCategorySpending.length > 0 &&
                activeCategorySpending.some((cat) => cat.amount > 0) ? (
                <Pie data={spendingByCategoryChart} options={chartOptions} />
              ) : (
                <div className="chart-empty-state">
                  <FaChartPie className="empty-chart-icon" />
                  <p>No category data yet</p>
                  <button
                    onClick={() => setShowAddExpenseModal(true)}
                    className="btn-primary small"
                  >
                    Add Expenses
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="recent-transactions" data-tour="recent-transactions">
          <div className="section-header">
            <div>
              <span className="section-eyebrow">Latest Activity</span>
              <h3>Recent Transactions</h3>
              <p className="section-subtitle">
                {activeRecentTransactions.length} transactions this month
              </p>
            </div>

            <button
              onClick={() => navigate("/transactions")}
              className="view-all-btn"
            >
              View All ({activeRecentTransactions.length})
            </button>
          </div>

          {activeRecentTransactions.length > 0 ? (
            <div className="transactions-grid">
              {activeRecentTransactions.slice(0, 6).map((transaction, index) => (
                <div key={index} className="transaction-card">
                  <div className="transaction-icon">
                    <div className={`icon-bg ${transaction.type}`}>
                      {transaction.type === "expense" ? "-" : "+"}
                    </div>
                  </div>

                  <div className="transaction-details">
                    <h4>
                      {transaction.isEncrypted ? (
                        <div className="encrypted-note-preview">
                          {decryptedNotes[transaction._id] ? (
                            <>
                              <FaUnlock className="vault-tiny-icon text-green-500" />
                              {decryptedNotes[transaction._id]}
                            </>
                          ) : (
                            <button onClick={() => handleDecryptClick(transaction)} className="unlock-note-btn">
                              <FaLock className="vault-tiny-icon" /> Locked Note
                            </button>
                          )}
                        </div>
                      ) : (
                        transaction.description || transaction.category || "Transaction"
                      )}
                    </h4>

                    <p className="transaction-category">
                      {transaction.category}
                    </p>
                    <p className="transaction-date">
                      {formatTransactionDate(transaction.date)}
                    </p>
                  </div>
                  <div className={`transaction-amount ${transaction.type}`}>
                    {transaction.type === "expense" ? "-" : "+"}
                    {formatCurrency(transaction.amount)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No transactions yet. Add your first expense or income!</p>
              <div className="empty-actions">
                <button
                  onClick={() => setShowAddExpenseModal(true)}
                  className="btn-primary"
                >
                  Add Expense
                </button>
                <button
                  onClick={() => setShowAddIncomeModal(true)}
                  className="btn-secondary"
                >
                  Add Income
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Savings Goals Summary */}
        {activeSavingsGoals.length > 0 ? (
          <div className="savings-summary">
            <div className="section-header">
              <div>
                <span className="section-eyebrow">Future Planning</span>
                <h3>Savings Goals</h3>
              </div>
              <span className="section-subtitle">Progress towards targets</span>
            </div>

            <div className="goals-grid">
              {activeSavingsGoals.slice(0, 3).map((goal, index) => {
                const progress = Math.min(goal.progress || 0, 100);
                const targetTime = new Date(goal.targetDate).getTime();
                const daysRemaining = Number.isNaN(targetTime)
                  ? null
                  : Math.max(0, Math.ceil((targetTime - Date.now()) / 86400000));

                return (
                <div key={index} className="goal-card">
                  <div className="goal-header">
                    <h4>{goal.name}</h4>
                    <span className="goal-category">{goal.category}</span>
                  </div>

                  <div className="goal-progress">
                    <div
                      className="goal-progress-ring"
                      style={{
                        "--goal-progress": `${progress * 3.6}deg`,
                      }}
                      aria-label={`${Math.round(progress)} percent saved`}
                    >
                      <span>{Math.round(progress)}%</span>
                    </div>

                    <div className="progress-bar">
                      <div
                        className="progress-fill green"
                        style={{
                          width: `${progress}%`,
                        }}
                      ></div>
                    </div>

                    <div className="goal-amounts">
                      <span className="current-amount">
                        {formatCurrency(goal.currentAmount)}
                      </span>
                      <span className="target-amount">
                        of {formatCurrency(goal.targetAmount)}
                      </span>
                    </div>

                    <div className="goal-date">
                      <span>
                        Target:{" "}
                        {new Date(goal.targetDate).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                      {daysRemaining !== null && (
                        <span className="goal-countdown">
                          <FaClock />
                          {daysRemaining} days left
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {/* MODALS */}
      <AddExpense
        isOpen={showAddExpenseModal}
        onClose={() => setShowAddExpenseModal(false)}
        onSuccess={handleAddExpense}
      />

      <AddIncome
        isOpen={showAddIncomeModal}
        onClose={() => setShowAddIncomeModal(false)}
        onSuccess={handleAddIncome}
      />

      <SetBudget
        isOpen={showSetBudgetModal}
        onClose={() => setShowSetBudgetModal(false)}
        onSetBudget={() => handleSuccess('budget')}
      />

      <SavingGoal
        isOpen={showSavingsGoalModal}
        onClose={() => setShowSavingsGoalModal(false)}
        onGoalCreated={() => handleSuccess('goal')}
      />

      {showVaultUnlock && (
        <VaultUnlock
          onClose={() => setShowVaultUnlock(false)}
          onSuccess={handleUnlockVaultSuccess}
        />
      )}

      <GuidedTour
        isOpen={isTourOpen}
        steps={tourSteps}
        onClose={handleTourClose}
      />
    </div>
  );
};

export default Dashboard;

